import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Space, Typography } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import { fetchSignedMediaUrl } from '../utils/media';
import AudioWaveform from './AudioWaveform';

const { Text } = Typography;

interface SegmentAudioPlayerProps {
  audioId: string;
  deviceId: string;
  startSec: number;
  endSec: number;
  inline?: boolean;
  label?: string;
  onProgress?: (relativeTimeSec: number) => void;
  onPlayStateChange?: (isPlaying: boolean) => void;
  seekRequest?: { timeSec: number; play?: boolean; token: number } | null;
}

const SegmentAudioPlayer: React.FC<SegmentAudioPlayerProps> = ({
  audioId,
  deviceId,
  startSec,
  endSec,
  inline = false,
  label,
  onProgress,
  onPlayStateChange,
  seekRequest = null,
}) => {
  const clampedStart = Math.max(0, startSec);
  const clampedEnd = Math.max(clampedStart, endSec);
  const clipDuration = clampedEnd - clampedStart || 0;
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shouldLoadAudio, setShouldLoadAudio] = useState(false);
  const [showWaveform, setShowWaveform] = useState(false);
  const [currentTime, setCurrentTime] = useState(clampedStart);
  const [audioDuration, setAudioDuration] = useState(clampedEnd);
  const audioRef = useRef<HTMLAudioElement>(null);
  const pendingPlayRef = useRef(false);
  const pendingSeekRef = useRef<{ timeSec: number; play: boolean } | null>(null);

  useEffect(() => {
    if (!shouldLoadAudio) {
      return;
    }
    let cancelled = false;
    const loadUrl = async () => {
      setLoading(true);
      try {
        const { fetchAuthSession } = await import('../auth/gateway');
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          return;
        }
        const signedUrl = await fetchSignedMediaUrl(
          `/api/v1/devices/${deviceId}/audio/${audioId}/file`,
          token,
        );
        if (!cancelled) {
          setAudioUrl(signedUrl);
          setCurrentTime(clampedStart);
          setAudioDuration(clampedEnd);
        }
      } catch (error) {
        console.error('Failed to load segment audio url', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadUrl();
    return () => {
      cancelled = true;
    };
  }, [audioId, clampedEnd, clampedStart, deviceId, shouldLoadAudio]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setAudioDuration(audio.duration || clampedEnd);
      const pendingSeek = pendingSeekRef.current;
      const nextTime = pendingSeek
        ? Math.max(clampedStart, Math.min(clampedEnd, clampedStart + pendingSeek.timeSec))
        : clampedStart;
      audio.currentTime = nextTime;
      setCurrentTime(nextTime);
      const shouldPlay = pendingSeek?.play || pendingPlayRef.current;
      pendingSeekRef.current = null;
      pendingPlayRef.current = false;
      if (shouldPlay) {
        audio.play()
          .then(() => {
            setIsPlaying(true);
            onPlayStateChange?.(true);
          })
          .catch((error) => console.error('Segment audio playback error', error));
      }
    };
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      const relative = audio.currentTime - clampedStart;
      onProgress?.(Math.max(0, relative));
      if (audio.currentTime >= clampedEnd) {
        audio.pause();
        audio.currentTime = clampedStart;
        setCurrentTime(clampedStart);
        setIsPlaying(false);
        onPlayStateChange?.(false);
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      audio.currentTime = clampedStart;
      setCurrentTime(clampedStart);
      onPlayStateChange?.(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [clampedStart, clampedEnd, onProgress, onPlayStateChange]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!audioUrl) {
      pendingPlayRef.current = true;
      setShouldLoadAudio(true);
      return;
    }
    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
        onPlayStateChange?.(false);
      } else {
        audio.currentTime = clampedStart;
        await audio.play();
        setIsPlaying(true);
        onPlayStateChange?.(true);
      }
    } catch (error) {
      console.error('Segment audio playback error', error);
    }
  }, [audioUrl, clampedStart, isPlaying, onPlayStateChange]);

  const handleWaveformSeek = useCallback((nextTime: number) => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    const clampedTime = Math.max(clampedStart, Math.min(clampedEnd, nextTime));
    audio.currentTime = clampedTime;
    setCurrentTime(clampedTime);
    onProgress?.(Math.max(0, clampedTime - clampedStart));
  }, [clampedEnd, clampedStart, onProgress]);

  const formatTime = (seconds: number) => {
    if (!Number.isFinite(seconds)) return '--:--';
    const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
    const ss = String(Math.floor(seconds % 60)).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  const lastSeekTokenRef = useRef<number | null>(null);
  useEffect(() => {
    if (!seekRequest) {
      return;
    }
    if (seekRequest.token && lastSeekTokenRef.current === seekRequest.token) {
      return;
    }
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (!audioUrl) {
      pendingSeekRef.current = {
        timeSec: seekRequest.timeSec,
        play: !!seekRequest.play,
      };
      setShouldLoadAudio(true);
      setShowWaveform(true);
      return;
    }
    const clampedRelative = Math.max(0, Math.min(clipDuration, seekRequest.timeSec));
    audio.currentTime = clampedStart + clampedRelative;
    setCurrentTime(audio.currentTime);
    if (seekRequest.play) {
      audio
        .play()
        .then(() => {
          setIsPlaying(true);
          onPlayStateChange?.(true);
        })
        .catch((err) => console.error('Segment audio seek play error', err));
    } else {
      setIsPlaying(false);
      onPlayStateChange?.(false);
    }
    lastSeekTokenRef.current = seekRequest.token ?? null;
  }, [seekRequest, audioUrl, clipDuration, clampedStart, onPlayStateChange]);

  const toggleWaveform = useCallback(() => {
    setShouldLoadAudio(true);
    setShowWaveform((prev) => !prev);
  }, []);

  const content = (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Space size={8} wrap>
        <Button
          size="small"
          type="primary"
          shape="circle"
          icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
          onClick={togglePlay}
          loading={loading}
          disabled={!audioUrl || clipDuration <= 0}
        />
        <Text type="secondary">
          {label ? `${label} ` : ''}
          {formatTime(clampedStart)} - {formatTime(clampedEnd)} (約 {clipDuration.toFixed(1)} 秒)
        </Text>
        <Button
          size="small"
          onClick={toggleWaveform}
          disabled={clipDuration <= 0}
        >
          {showWaveform ? '波形を隠す' : '波形を表示'}
        </Button>
      </Space>
      {showWaveform && audioUrl ? (
        <AudioWaveform
          audioUrl={audioUrl}
          currentTime={currentTime}
          duration={audioDuration}
          onSeek={handleWaveformSeek}
          clipStartSec={clampedStart}
          clipEndSec={clampedEnd}
        />
      ) : null}
    </Space>
  );

  return (
    <div style={{ width: '100%' }}>
      <audio ref={audioRef} src={audioUrl || undefined} preload="metadata" />
      {inline ? content : <div>{content}</div>}
    </div>
  );
};

export default SegmentAudioPlayer;
